import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlModalProps, NakedButton, Text } from '@frontend/design-system';
import { SelfUpgradeActionType } from '../../constants/self-upgrade';
import { getFeaturePromoActionTrackingId, UpgradeActionButtonTrackingIdMap } from '../../constants/tracking-ids';
import { useGetSelfUpgradeAction } from '../../hooks/useGetSelfUpgradeAction';
import { useFeatureSubscription } from '../../providers/FeatureSubscriptionProvider';
import { getBundleTrialDays, getUpgradeBundleDisplayName } from '../../self-upgrade.util';
import { FeatureUpgradeIcon } from '../feature-upgrade-icon/FeatureUpgradeIcon';

interface FeatureDemoModalProps extends ModalControlModalProps {
  onUpgradeAction?: () => void;
  contentStyles?: Interpolation<Theme>;
}
export const FeatureDemoModal: FC<PropsWithChildren<FeatureDemoModalProps>> = ({
  children,
  contentStyles,
  onUpgradeAction,
  ...modalProps
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'demo-modal' });
  const { feature, isBundleUpgradeEligible, recommendedBundle } = useFeatureSubscription();
  const { actionIconName, actionType, actionTextData, isLoading } = useGetSelfUpgradeAction();

  const getTrackingId = useCallback(
    (action: keyof typeof UpgradeActionButtonTrackingIdMap) => {
      return getFeaturePromoActionTrackingId({
        feature,
        action,
        source: 'demo-modal',
        upgradeType: isBundleUpgradeEligible ? 'bundle' : 'feature',
      });
    },
    [feature, isBundleUpgradeEligible]
  );

  const actionButtonTrackingId = useMemo<string | undefined>(
    () => getTrackingId(actionType),
    [actionType, getTrackingId]
  );

  const handleUpgradeAction = () => {
    onUpgradeAction?.();
    modalProps.onClose();
  };

  return (
    <Modal
      {...modalProps}
      css={styles.modal}
      // This value of z-index allows panels to open above demo modal
      zIndex={theme.zIndex.modals - 2}
      asChild
    >
      <div css={styles.modal}>
        <header css={styles.header}>
          <div css={styles.headerTitle}>
            <div css={styles.headerBadgeTitle}>
              <FeatureUpgradeIcon css={{ aspectRatio: 1, width: 20 }} />
              <Text weight='bold' size='large'>
                {t('This is a demo using sample data.')}
              </Text>
            </div>
            {isBundleUpgradeEligible && actionType !== SelfUpgradeActionType.NONE && (
              <Text as='span' size='medium' weight='bold' css={styles.headerSubtext}>
                {t('Included in the {{bundleName}} Bundle. Try {{bundleName}} free for {{count}} days.', {
                  bundleName: getUpgradeBundleDisplayName(recommendedBundle),
                  count: getBundleTrialDays(recommendedBundle),
                })}
              </Text>
            )}
          </div>

          {!isLoading && actionType !== SelfUpgradeActionType.NONE && (
            <Button
              variant={isBundleUpgradeEligible ? 'primary' : 'tertiary'}
              iconName={isBundleUpgradeEligible ? actionIconName : undefined}
              trackingId={actionButtonTrackingId}
              onClick={handleUpgradeAction}
              size='small'
              css={{ gridArea: 'action' }}
            >
              {actionTextData?.primaryButtonText ?? ''}
            </Button>
          )}
          <NakedButton trackingId={getTrackingId('DISMISS')} onClick={modalProps.onClose} css={styles.closeBtn}>
            <Icon name='x' size={16} />
          </NakedButton>
        </header>
        <div css={[styles.body, contentStyles]}>{children}</div>
      </div>
    </Modal>
  );
};

const styles = {
  modal: css`
    padding: 0;
    min-width: min(85vw, 1200px);
    height: calc(100vh - 64px);
    overflow: auto;
    display: flex;
    flex-direction: column;

    @media (max-width: ${breakpoints.xsmall.max}px) {
      width: calc(100vw - ${theme.spacing(4)});
    }
    > * {
      min-height: unset;
      min-width: unset;
    }
  `,
  header: css`
    background-color: ${theme.colors.secondary.seaweed5};
    padding: ${theme.spacing(1, 2)};
    border-bottom: 1px solid ${theme.colors.secondary.seaweed40};
    gap: ${theme.spacing(2)};
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-template-rows: auto;
    grid-template-areas: 'title action close';
    align-items: center;
  `,
  headerSubtext: css`
    @media (max-width: ${breakpoints.small.max}px) {
      display: none;
    }
  `,
  headerTitle: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
    grid-area: title;
  `,
  headerBadgeTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  closeBtn: css`
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.full};
    padding: ${theme.spacing(1)};
    grid-area: close;
  `,
  body: css`
    padding: 0;
    margin: 0;
    overflow: auto;
    scrollbar-gutter: stable;
  `,
};
